/**
 * Craft Tab Content for GameMenu
 * Responsive design for all screen sizes
 */

import { MenuTabContent } from '../MenuTabContent';
import { Player } from '@client/entities/Player';
import { CraftingRecipe, CRAFTING_RECIPES, CraftingSystem, CraftingCurrencyType } from '@shared/crafting/CraftingSystem';
import { EquipmentItem } from '@shared/types';
import { t } from '@locales/index';
import { formatNumber } from '@shared/utils';

export class CraftTab extends MenuTabContent {
  private selectedRecipeIndex: number = 0;
  private hoveredRecipe: number = -1;
  private recipeScroll: number = 0;
  private craftingProgress: number = 0;
  private isCrafting: boolean = false;
  private selectedInputItem: EquipmentItem | null = null;
  private resultMessage: string = '';
  private resultSuccess: boolean = false;
  private resultTimer: number = 0;
  private hoveredCraftButton: boolean = false;
  private hoveredInputSlot: boolean = false;
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;

  // Lock feature for Sabitleme Tasi
  private lockPrefixes: boolean = false;
  private lockSuffixes: boolean = false;
  private hoveredLockButton: boolean = false;

  // Map recipe IDs to CraftingCurrencyType for CraftingSystem
  private static readonly RECIPE_TO_CRAFT_ACTION: Record<string, CraftingCurrencyType> = {
    'upgrade_magic': 'demir_parcasi',
    'upgrade_rare': 'ruh_damlasi',
    'reroll_mods': 'kam_boyasi',
    'add_prefix': 'bereket_tozu',
    'add_suffix': 'ugur_tozu',
  };

  constructor(player: Player) {
    super(player);
  }

  measureSize(_ctx: CanvasRenderingContext2D): { width: number; height: number } {
    return { width: 850, height: 500 };
  }

  update(deltaTime: number): void {
    if (this.isCrafting) {
      this.craftingProgress += deltaTime * 2;
      if (this.craftingProgress >= 1) {
        this.completeCraft();
      }
    }
    if (this.resultTimer > 0) {
      this.resultTimer -= deltaTime;
    }
  }

  onActivate(): void {
    this.isCrafting = false;
    this.craftingProgress = 0;
  }

  // Get responsive layout
  private getLayout() {
    const { width, height } = this.bounds;
    const padding = Math.min(20, width * 0.02);
    const listWidth = Math.min(320, Math.max(220, width * 0.35));
    const detailsWidth = width - listWidth - padding * 3;
    const maxDetailsWidth = 450;
    const actualDetailsWidth = Math.min(detailsWidth, maxDetailsWidth);
    const detailsX = listWidth + padding * 2 + (detailsWidth - actualDetailsWidth) / 2;

    return {
      padding,
      listWidth,
      listX: padding,
      listY: padding,
      listHeight: height - padding * 2,
      detailsX,
      detailsY: padding,
      detailsWidth: actualDetailsWidth,
      detailsHeight: height - padding * 2,
      fontSize: Math.max(11, Math.min(14, width / 80)),
      titleSize: Math.max(14, Math.min(18, width / 60)),
      itemHeight: Math.max(45, Math.min(55, height / 9)),
    };
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    const { x, y } = this.bounds;
    const layout = this.getLayout();

    // Left - Recipe list
    this.renderRecipeList(ctx, x + layout.listX, y + layout.listY, layout.listWidth, layout.listHeight);

    // Right - Recipe details
    const recipe = CRAFTING_RECIPES[this.selectedRecipeIndex];
    if (recipe) {
      this.renderRecipeDetails(ctx, recipe, x + layout.detailsX, y + layout.detailsY, layout.detailsWidth, layout.detailsHeight);
    }

    // Render tooltip for hovered recipe (drawn last, on top)
    if (this.hoveredRecipe >= 0 && this.hoveredRecipe !== this.selectedRecipeIndex) {
      const hoveredRecipe = CRAFTING_RECIPES[this.hoveredRecipe];
      if (hoveredRecipe) {
        this.renderRecipeTooltip(ctx, hoveredRecipe, this.lastMouseX, this.lastMouseY);
      }
    }
  }

  private renderRecipeTooltip(ctx: CanvasRenderingContext2D, recipe: CraftingRecipe, mouseX: number, mouseY: number): void {
    const layout = this.getLayout();
    const fontSize = layout.fontSize;
    const lineH = fontSize + 6;
    const padding = 10;

    // Calculate tooltip content lines
    const lines: { text: string; color: string }[] = [];
    lines.push({ text: recipe.name, color: '#d4af37' });
    if (recipe.description) {
      lines.push({ text: recipe.description, color: '#a0a090' });
    }
    lines.push({ text: '', color: '' }); // spacer
    lines.push({ text: t('crafting.requirements'), color: '#808070' });
    if (recipe.goldCost > 0) {
      const hasGold = this.player.inventory.getGold() >= recipe.goldCost;
      lines.push({ text: `  ${t('ui.gold')}: ${formatNumber(recipe.goldCost)}`, color: hasGold ? '#ffd700' : '#ff4444' });
    }
    for (const mat of recipe.materials) {
      const has = this.getMaterialCount(mat.itemId) >= mat.amount;
      lines.push({ text: `  ${mat.name}: ${mat.amount}`, color: has ? '#b0a090' : '#ff4444' });
    }
    if (recipe.requiresInput) {
      lines.push({ text: `  ${t('crafting.rightClickInventory')}`, color: '#9090a0' });
    }

    // Measure tooltip size
    ctx.font = `${fontSize}px Arial`;
    let maxWidth = 0;
    for (const line of lines) {
      const w = ctx.measureText(line.text).width;
      if (w > maxWidth) maxWidth = w;
    }
    const tooltipW = maxWidth + padding * 2;
    const tooltipH = lines.length * lineH + padding * 2;

    // Position tooltip - prefer right of cursor, clamp to bounds
    let tx = mouseX + 12;
    let ty = mouseY - tooltipH / 2;
    const { x: bx, y: by, width: bw, height: bh } = this.bounds;
    if (tx + tooltipW > bx + bw) tx = mouseX - tooltipW - 12;
    if (ty < by) ty = by;
    if (ty + tooltipH > by + bh) ty = by + bh - tooltipH;

    // Draw tooltip background
    ctx.save();
    ctx.fillStyle = '#1a1510';
    ctx.globalAlpha = 0.95;
    this.drawRoundedRect(ctx, tx, ty, tooltipW, tooltipH, 5);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = '#d4af37';
    ctx.lineWidth = 1;
    this.drawRoundedRect(ctx, tx, ty, tooltipW, tooltipH, 5);
    ctx.stroke();

    // Draw tooltip text
    ctx.textAlign = 'left';
    let textY = ty + padding + fontSize;
    for (const line of lines) {
      if (line.text) {
        ctx.fillStyle = line.color;
        ctx.font = `${fontSize}px Arial`;
        ctx.fillText(line.text, tx + padding, textY);
      }
      textY += lineH;
    }
    ctx.restore();
  }

  private renderRecipeList(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    const layout = this.getLayout();

    // Title
    ctx.fillStyle = '#d4af37';
    ctx.font = `bold ${layout.titleSize}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText(t('crafting.recipes'), x, y + 18);

    // List background
    const listY = y + 35;
    const listH = h - 35;
    ctx.fillStyle = '#0d0a08';
    this.drawRoundedRect(ctx, x, listY, w, listH, 5);
    ctx.fill();
    ctx.strokeStyle = '#3d3228';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Clip
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, listY, w, listH);
    ctx.clip();

    const itemH = layout.itemHeight;
    for (let i = 0; i < CRAFTING_RECIPES.length; i++) {
      const recipe = CRAFTING_RECIPES[i];
      const itemY = listY + i * itemH - this.recipeScroll;
      if (itemY + itemH < listY || itemY > listY + listH) continue;

      const isSelected = i === this.selectedRecipeIndex;
      const isHovered = i === this.hoveredRecipe;
      const canCraft = this.canCraft(recipe);

      if (isSelected) {
        ctx.fillStyle = '#2a2420';
        this.drawRoundedRect(ctx, x + 4, itemY + 2, w - 8, itemH - 4, 3);
        ctx.fill();
        ctx.strokeStyle = '#d4af37';
        ctx.lineWidth = 1;
        ctx.stroke();
      } else if (isHovered) {
        ctx.fillStyle = '#221e1a';
        this.drawRoundedRect(ctx, x + 4, itemY + 2, w - 8, itemH - 4, 3);
        ctx.fill();
        ctx.strokeStyle = '#a08860';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Recipe name
      ctx.fillStyle = canCraft ? (isSelected ? '#fff' : '#b0a090') : '#505050';
      ctx.font = `${layout.fontSize}px Arial`;
      ctx.textAlign = 'left';
      ctx.fillText(recipe.name, x + 15, itemY + itemH * 0.42);

      // Category
      ctx.fillStyle = '#606050';
      ctx.font = `${layout.fontSize - 2}px Arial`;
      ctx.fillText(this.getCategoryName(recipe.category), x + 15, itemY + itemH * 0.75);

      // Cost
      if (recipe.goldCost > 0) {
        ctx.fillStyle = '#ffd700';
        ctx.font = `${layout.fontSize - 1}px Arial`;
        ctx.textAlign = 'right';
        ctx.fillText(`${formatNumber(recipe.goldCost)}g`, x + w - 12, itemY + itemH * 0.55);
      }
    }

    ctx.restore();
  }

  private renderRecipeDetails(ctx: CanvasRenderingContext2D, recipe: CraftingRecipe, x: number, y: number, w: number, h: number): void {
    const layout = this.getLayout();

    // Background
    ctx.fillStyle = '#1a1510';
    this.drawRoundedRect(ctx, x, y, w, h, 8);
    ctx.fill();
    ctx.strokeStyle = '#3d3228';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Title
    ctx.fillStyle = '#d4af37';
    ctx.font = `bold ${layout.titleSize + 2}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText(recipe.name, x + 20, y + 35);

    // Description
    if (recipe.description) {
      ctx.fillStyle = '#808070';
      ctx.font = `${layout.fontSize}px Arial`;
      ctx.fillText(recipe.description, x + 20, y + 58);
    }

    // Requirements
    ctx.fillStyle = '#606050';
    ctx.font = `bold ${layout.fontSize}px Arial`;
    ctx.fillText('GEREKSINIMLER', x + 20, y + 95);

    let reqY = y + 118;
    const lineH = Math.max(22, layout.fontSize + 8);

    // Gold cost
    if (recipe.goldCost > 0) {
      const hasGold = this.player.inventory.getGold() >= recipe.goldCost;
      ctx.fillStyle = hasGold ? '#ffd700' : '#ff4444';
      ctx.font = `${layout.fontSize}px Arial`;
      ctx.fillText(`${t('ui.gold')}: ${formatNumber(recipe.goldCost)}`, x + 25, reqY);
      reqY += lineH;
    }

    // Material costs
    for (const mat of recipe.materials) {
      const hasAmount = this.getMaterialCount(mat.itemId) >= mat.amount;
      ctx.fillStyle = hasAmount ? '#b0a090' : '#ff4444';
      ctx.font = `${layout.fontSize}px Arial`;
      ctx.fillText(`${mat.name}: ${mat.amount}`, x + 25, reqY);
      reqY += lineH;
    }

    // Input item slot for recipes that require an item
    if (recipe.requiresInput) {
      reqY += 5;
      ctx.fillStyle = '#606050';
      ctx.font = `bold ${layout.fontSize}px Arial`;
      ctx.fillText('ESYA', x + 20, reqY);
      reqY += 20;

      // Input item slot
      const slotSize = 44;
      const slotX = x + 25;
      const slotY = reqY;

      ctx.fillStyle = this.hoveredInputSlot ? '#151210' : '#0d0a08';
      this.drawRoundedRect(ctx, slotX, slotY, slotSize, slotSize, 4);
      ctx.fill();
      ctx.strokeStyle = this.selectedInputItem ? '#d4af37' : (this.hoveredInputSlot ? '#a08860' : '#3d3228');
      ctx.lineWidth = this.selectedInputItem ? 2 : (this.hoveredInputSlot ? 1.5 : 1);
      ctx.stroke();

      if (this.selectedInputItem) {
        // Show selected item color by rarity
        const rarityColors: Record<string, string> = {
          normal: '#aaaaaa', magic: '#4488ff', rare: '#ffdd44',
          sacred: '#44ff88', legendary: '#ff8844',
        };
        const color = rarityColors[this.selectedInputItem.rarity] || '#aaaaaa';
        ctx.fillStyle = color;
        ctx.fillRect(slotX + 6, slotY + 6, slotSize - 12, slotSize - 12);

        // Item name next to slot
        ctx.fillStyle = color;
        ctx.font = `${layout.fontSize}px Arial`;
        ctx.textAlign = 'left';
        const displayName = this.selectedInputItem.name;
        ctx.fillText(displayName, slotX + slotSize + 10, slotY + slotSize / 2 + 4);
      } else {
        ctx.fillStyle = '#505050';
        ctx.font = `${layout.fontSize - 1}px Arial`;
        ctx.textAlign = 'left';
        ctx.fillText(t('crafting.rightClickInventory'), slotX + slotSize + 10, slotY + slotSize / 2 + 4);
      }

      reqY += slotSize + 10;
    }

    // Lock toggle for reroll recipe (kam_boyasi)
    if (recipe.id === 'reroll_mods' && recipe.requiresInput) {
      const lockBtnX = x + 20;
      const lockBtnY = reqY;
      const lockBtnW = w - 40;
      const lockBtnH = 36;
      const lockCount = this.player.inventory.getCraftingCurrencyCount('sabitleme_tasi');
      const hasLock = lockCount > 0;
      const lockActive = this.lockPrefixes || this.lockSuffixes;

      // Lock button background
      ctx.fillStyle = lockActive ? '#2a2a10' : (this.hoveredLockButton ? '#1a1815' : '#141210');
      this.drawRoundedRect(ctx, lockBtnX, lockBtnY, lockBtnW, lockBtnH, 5);
      ctx.fill();
      ctx.strokeStyle = lockActive ? '#ffd700' : (hasLock ? '#806020' : '#333');
      ctx.lineWidth = lockActive ? 2 : 1;
      ctx.stroke();

      // Lock icon and text
      const lockText = this.lockPrefixes ? '\uD83D\uDD12 Prefix Sabitlendi'
        : this.lockSuffixes ? '\uD83D\uDD12 Suffix Sabitlendi'
        : `\uD83D\uDD13 Sabitle (${lockCount})`;
      ctx.fillStyle = lockActive ? '#ffd700' : (hasLock ? '#a08040' : '#555');
      ctx.font = `bold ${layout.fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(lockText, lockBtnX + lockBtnW / 2, lockBtnY + lockBtnH / 2 + 5);

      reqY += lockBtnH + 10;
    }

    // Result message
    if (this.resultTimer > 0) {
      const alpha = Math.min(1, this.resultTimer);
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = this.resultSuccess ? '#44ff44' : '#ff4444';
      ctx.font = `bold ${layout.fontSize + 1}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(this.resultMessage, x + w / 2, y + h - 85);
      ctx.restore();
    }

    // Craft button
    const canCraft = this.canCraft(recipe);
    const btnW = Math.min(w - 40, 200);
    const btnH = 50;
    const btnX = x + (w - btnW) / 2;
    const btnY = y + h - btnH - 25;

    if (this.isCrafting) {
      // Progress bar
      ctx.fillStyle = '#1a1510';
      this.drawRoundedRect(ctx, btnX, btnY, btnW, btnH, 5);
      ctx.fill();
      ctx.strokeStyle = '#3d3228';
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.fillStyle = '#d4af37';
      this.drawRoundedRect(ctx, btnX + 3, btnY + 3, (btnW - 6) * this.craftingProgress, btnH - 6, 3);
      ctx.fill();

      ctx.fillStyle = '#fff';
      ctx.font = `bold ${layout.fontSize + 1}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(t('crafting.craftingInProgress'), btnX + btnW / 2, btnY + btnH / 2 + 5);
    } else {
      const btnHover = this.hoveredCraftButton && canCraft;
      ctx.fillStyle = canCraft ? (btnHover ? '#225a22' : '#1a4a1a') : '#1a1a1a';
      this.drawRoundedRect(ctx, btnX, btnY, btnW, btnH, 5);
      ctx.fill();
      ctx.strokeStyle = canCraft ? (btnHover ? '#44ee88' : '#2ecc71') : '#404040';
      ctx.lineWidth = canCraft ? (btnHover ? 2.5 : 2) : 1;
      ctx.stroke();

      ctx.fillStyle = canCraft ? '#2ecc71' : '#505050';
      ctx.font = `bold ${layout.titleSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(t('crafting.craftBtn'), btnX + btnW / 2, btnY + btnH / 2 + 6);
    }
  }

  private canCraft(recipe: CraftingRecipe): boolean {
    // Check gold
    if (recipe.goldCost > 0 && this.player.inventory.getGold() < recipe.goldCost) {
      return false;
    }

    // Check materials
    for (const mat of recipe.materials) {
      if (this.getMaterialCount(mat.itemId) < mat.amount) {
        return false;
      }
    }

    // Check input item for recipes that require it
    if (recipe.requiresInput && !this.selectedInputItem) {
      return false;
    }

    return true;
  }

  private getMaterialCount(itemId: string): number {
    return this.player.inventory.getCraftingCurrencyCount(itemId as CraftingCurrencyType);
  }

  private getCategoryName(category: string): string {
    const names: Record<string, string> = {
      weapon: 'Silah', armor: 'Zirh', accessory: 'Aksesuar',
      consumable: 'Tuketilebilir', material: 'Malzeme', upgrade: 'Gelistirme',
    };
    return names[category] || category;
  }

  private completeCraft(): void {
    this.isCrafting = false;
    this.craftingProgress = 0;

    const recipe = CRAFTING_RECIPES[this.selectedRecipeIndex];
    if (!recipe || !this.canCraft(recipe)) return;

    // Deduct gold
    if (recipe.goldCost > 0) {
      this.player.inventory.removeGold(recipe.goldCost);
    }

    // Deduct materials (from crafting currencies)
    for (const mat of recipe.materials) {
      this.player.inventory.removeCraftingCurrency(mat.itemId as CraftingCurrencyType, mat.amount);
    }

    // Apply recipe effect
    if (recipe.requiresInput) {
      // Recipes that modify existing items
      if (!this.selectedInputItem) {
        this.showResult(t('crafting.noItemSelected'), false);
        return;
      }

      const craftAction = CraftTab.RECIPE_TO_CRAFT_ACTION[recipe.id];
      if (craftAction) {
        const result = this.applyCraftAction(craftAction, this.selectedInputItem);
        this.showResult(result.message, result.success);
      }
    } else {
      // Recipes that produce new items (potions, etc.)
      // For consumables, just show success - potion system handles charges separately
      this.showResult(t('crafting.recipeSuccess', { name: recipe.name }), true);
    }
  }

  private applyCraftAction(action: CraftingCurrencyType, item: EquipmentItem): { success: boolean; message: string } {
    switch (action) {
      case 'demir_parcasi':
        return CraftingSystem.applyDemirParcasi(item);
      case 'ruh_damlasi':
        return CraftingSystem.applyRuhDamlasi(item);
      case 'kam_boyasi': {
        // Consume Sabitleme Tasi if lock is active
        if (this.lockPrefixes || this.lockSuffixes) {
          const lockCount = this.player.inventory.getCraftingCurrencyCount('sabitleme_tasi');
          if (lockCount <= 0) {
            this.lockPrefixes = false;
            this.lockSuffixes = false;
            return { success: false, message: 'Sabitleme Tasi yok! Kilit kaldirildi.' };
          }
          this.player.inventory.removeCraftingCurrency('sabitleme_tasi', 1);
        }
        const result = CraftingSystem.applyKamBoyasi(item, this.lockPrefixes, this.lockSuffixes);
        return result;
      }
      case 'bereket_tozu':
        return CraftingSystem.applyBereketTozu(item);
      case 'ugur_tozu':
        return CraftingSystem.applyUgurTozu(item);
      default:
        return { success: false, message: 'Bilinmeyen islem' };
    }
  }

  private showResult(message: string, success: boolean): void {
    this.resultMessage = message;
    this.resultSuccess = success;
    this.resultTimer = 3;
  }

  handleMouseEvent(e: import('../components/UIComponent').MouseEvent): boolean {
    if (!this.visible) return false;

    const { x, y } = this.bounds;
    const layout = this.getLayout();

    // Recipe list area
    const listX = x + layout.listX;
    const listY = y + layout.listY + 35;
    const listW = layout.listWidth;
    const listH = layout.listHeight - 35;
    const itemH = layout.itemHeight;

    // Craft button bounds (computed once for hover and click)
    const recipe = CRAFTING_RECIPES[this.selectedRecipeIndex];
    const btnW = Math.min(layout.detailsWidth - 40, 200);
    const btnH = 50;
    const btnX = x + layout.detailsX + (layout.detailsWidth - btnW) / 2;
    const btnY = y + layout.detailsY + layout.detailsHeight - btnH - 25;

    // Input slot bounds (approximation: rendered inside renderRecipeDetails)
    const inputSlotSize = 44;
    const inputSlotX = x + layout.detailsX + 25;
    // The input slot Y varies by recipe content, but we can approximate its area
    const detailsAreaY = y + layout.detailsY;

    if (e.type === 'move') {
      this.lastMouseX = e.x;
      this.lastMouseY = e.y;

      // Reset hover states
      this.hoveredRecipe = -1;
      this.hoveredCraftButton = false;
      this.hoveredInputSlot = false;
      this.hoveredLockButton = false;

      // Recipe list hover
      if (e.x >= listX && e.x <= listX + listW && e.y >= listY && e.y <= listY + listH) {
        const relY = e.y - listY + this.recipeScroll;
        const index = Math.floor(relY / itemH);
        if (index >= 0 && index < CRAFTING_RECIPES.length) {
          this.hoveredRecipe = index;
        }
      }

      // Craft button hover
      if (e.x >= btnX && e.x <= btnX + btnW && e.y >= btnY && e.y <= btnY + btnH) {
        this.hoveredCraftButton = true;
      }

      // Input slot hover (if recipe requires input)
      if (recipe && recipe.requiresInput) {
        // Approximate Y position based on recipe detail layout
        const approxInputY = detailsAreaY + 120 + (recipe.goldCost > 0 ? 30 : 0) + recipe.materials.length * 28;
        if (e.x >= inputSlotX && e.x <= inputSlotX + inputSlotSize &&
            e.y >= approxInputY && e.y <= approxInputY + inputSlotSize) {
          this.hoveredInputSlot = true;
        }

        // Lock button hover (for reroll recipe)
        if (recipe.id === 'reroll_mods') {
          const lockBtnX = x + layout.detailsX + 20;
          const lockBtnY = approxInputY + inputSlotSize + 10;
          const lockBtnW = layout.detailsWidth - 40;
          const lockBtnH = 36;
          if (e.x >= lockBtnX && e.x <= lockBtnX + lockBtnW &&
              e.y >= lockBtnY && e.y <= lockBtnY + lockBtnH) {
            this.hoveredLockButton = true;
          }
        }
      }
    }

    if (e.type === 'down' && e.button === 'left') {
      // Recipe selection
      if (e.x >= listX && e.x <= listX + listW && e.y >= listY && e.y <= listY + listH) {
        const relY = e.y - listY + this.recipeScroll;
        const index = Math.floor(relY / itemH);
        if (index >= 0 && index < CRAFTING_RECIPES.length) {
          this.selectedRecipeIndex = index;
          return true;
        }
      }

      // Lock button click (for reroll recipe)
      if (recipe && recipe.id === 'reroll_mods' && recipe.requiresInput) {
        const approxInputY = detailsAreaY + 120 + (recipe.goldCost > 0 ? 30 : 0) + recipe.materials.length * 28;
        const lockBtnX = x + layout.detailsX + 20;
        const lockBtnY = approxInputY + inputSlotSize + 10;
        const lockBtnW = layout.detailsWidth - 40;
        const lockBtnH = 36;
        if (e.x >= lockBtnX && e.x <= lockBtnX + lockBtnW &&
            e.y >= lockBtnY && e.y <= lockBtnY + lockBtnH) {
          // Cycle: none -> lock prefix -> lock suffix -> none
          if (!this.lockPrefixes && !this.lockSuffixes) {
            this.lockPrefixes = true;
          } else if (this.lockPrefixes) {
            this.lockPrefixes = false;
            this.lockSuffixes = true;
          } else {
            this.lockSuffixes = false;
          }
          return true;
        }
      }

      // Craft button click
      if (e.x >= btnX && e.x <= btnX + btnW && e.y >= btnY && e.y <= btnY + btnH) {
        if (!this.isCrafting && recipe && this.canCraft(recipe)) {
          this.isCrafting = true;
          this.craftingProgress = 0;
        }
        return true;
      }
    }

    // Right-click to clear selected input item
    if (e.type === 'down' && e.button === 'right' && this.selectedInputItem) {
      this.selectedInputItem = null;
      return true;
    }

    if (e.type === 'wheel' && e.deltaY !== undefined) {
      this.recipeScroll = Math.max(0, this.recipeScroll + e.deltaY * 0.5);
      return true;
    }

    return false;
  }

  /**
   * Set the input item for crafting (called from InventoryTab on right-click while craft tab is active)
   */
  setInputItem(item: EquipmentItem): void {
    this.selectedInputItem = item;
  }

  getSelectedInputItem(): EquipmentItem | null {
    return this.selectedInputItem;
  }
}
